import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/brand";
import { clientTestimonials, recommendations, recommendationsUrl } from "@/lib/offer";

const differentials = [
  { title: "Um responsável, do início ao fim", text: "O fundador acompanha cada etapa e responde pelo resultado." },
  { title: "Equipe sob medida", text: "Especialistas escolhidos para o tamanho e o tipo do seu projeto." },
  { title: "Valor fechado", text: "Escopo, prazo e valor definidos antes de começar." },
  { title: "Rigor de sistema crítico", text: "Experiência em sistemas hospitalares aplicada ao seu projeto." },
];

export default function Founder() {
  return (
    <section id="equipe" data-testid="founder-section" className="bg-muted py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal as="figure" className="mx-auto w-full max-w-md lg:col-span-5 lg:mx-0">
            <picture>
              <source srcSet="/bruno-perfil.webp" type="image/webp" />
              <img
                src="/bruno-perfil.jpg"
                alt="Bruno Luz, fundador da Ambitie"
                loading="lazy"
                width="1100"
                height="1100"
                className="aspect-[4/5] w-full rounded-xl object-cover"
              />
            </picture>
            <figcaption className="mt-4 text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">Bruno Luz</span>, fundador da Ambitie
            </figcaption>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <h2 className="text-3xl font-bold tracking-[-0.03em] text-foreground md:text-5xl">
              Um responsável pelo seu projeto.{" "}
              <span className="text-primary">Uma equipe inteira por trás.</span>
            </h2>
            <p className="mt-6 max-w-[62ch] text-lg leading-relaxed text-muted-foreground">
              Muitas empresas têm sites que funcionam só como cartões de visita caros. A Ambitie
              nasceu para mudar isso. O Bruno, fundador, conduz cada projeto e monta a equipe certa
              para ele: design, desenvolvimento e conteúdo, com especialistas que já trabalham com a
              gente.
            </p>

            <dl className="mt-10 grid gap-x-8 gap-y-6 sm:grid-cols-2">
              {differentials.map((item, i) => (
                <div key={item.title} data-testid={`differential-${i}`} className="border-l-2 border-accent pl-4">
                  <dt className="font-semibold text-foreground">{item.title}</dt>
                  <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>

        {clientTestimonials.length > 0 && (
          <div data-testid="client-testimonials" className="mt-20 grid gap-6 md:grid-cols-2">
            {clientTestimonials.map((item, i) => (
              <Reveal
                key={item.name}
                as="figure"
                delay={i * 0.08}
                className="flex flex-col rounded-xl border border-border bg-background p-6 md:p-8"
              >
                {item.result && (
                  <p className="text-2xl font-bold tracking-[-0.03em] text-primary">{item.result}</p>
                )}
                <blockquote className="mt-3 flex-1 text-lg leading-relaxed text-foreground">
                  &ldquo;{item.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-3 text-sm">
                  {item.photo && (
                    <img
                      src={item.photo}
                      alt=""
                      loading="lazy"
                      width="48"
                      height="48"
                      className="h-12 w-12 rounded-full object-cover"
                    />
                  )}
                  <span>
                    <span className="block font-semibold text-foreground">{item.name}</span>
                    <span className="block text-muted-foreground">{item.role}</span>
                  </span>
                </figcaption>
              </Reveal>
            ))}
          </div>
        )}

        <div
          className={`grid gap-10 border-t border-border pt-12 md:grid-cols-2 ${
            clientTestimonials.length > 0 ? "mt-12" : "mt-20"
          }`}
        >
          {recommendations.map((item, i) => (
            <Reveal key={item.name} as="figure" delay={i * 0.08}>
              <blockquote className="text-lg leading-relaxed text-foreground">
                &ldquo;{item.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-4 text-sm">
                <span className="font-semibold text-foreground">{item.name}</span>
                <span className="block text-muted-foreground">{item.role}</span>
              </figcaption>
            </Reveal>
          ))}
        </div>
        <a
          href={recommendationsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 inline-flex items-center gap-1.5 py-2 text-sm font-semibold text-primary hover:underline"
        >
          Ler as recomendações no LinkedIn
          <ArrowUpRight className="h-4 w-4" />
        </a>
      </div>
    </section>
  );
}
